import React, { useState, useEffect, useRef } from 'react';
import { TextField, Button, Grid, FormHelperText, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { QuattrLogoSpinner } from './components/QuattrLogoSpinner';
import CustomerDropDownWithLock from './components/CustomerDropDownWithLock';
import { fetchAIBasedContent } from '../../utility/cr-common';
import { AxiosError } from 'axios';
import LockOpenIcon from '@material-ui/icons/Lock';
import ReCAPTCHA from 'react-google-recaptcha';
import { CONTINUE_USING_TEXT } from './components/ToolsStyleWrapper';
import {
  COMPONENT_NAMES,
  SCREEN_NAMES,
  CONTENT_AI_TOOL_NAME,
  CONTENT_AI_TOOL_CATEGORY,
  trackAnalyticsEvent,
} from '../../../src/constants/analyticsMeta';
import {
  getToolsUsageCreditsNeeded,
  handleGenerateOutput,
  PAID_TOOLS_PROMPT_FOR,
  resetCaptcha,
} from '../../utility/common';
import Cookies from 'js-cookie';
import { SkipCaptcha } from './components/SkipCaptcha';
import { useSession } from '../../utility/services/customHooks';
import { SUBSCRIPTION_STATUS } from '../../constants/common';
import CreditsInfo from './components/CreditsInfo';
import { PremiumToolsIcon } from '../../utility/tools-config';

const useStyles = makeStyles((theme) => ({
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const SEOFriendlyURL = ({
  classes,
  setShowSignUpPage,
  user,
  hasValidSubscription,
  resetLockedGrace,
  enableLockedOption,
  handleResetUsage,
  isQwaDashboard = false,
  isCreditsShown = false,
  creditsRemaining = 0,
  usagePerDay = 0,
  setShowBuyOnePass,
  setIsRedirectToWebflow,
  setShowMinimumCreditsNeededMsg = (a) => {},
  setShowVerifyEmailPopup,
  freeToolsUsage = 0,
  showFreeToolsUsage = false,
  isCorpEmail = false,
}) => {
  const ownClasses = useStyles();
  const qttr_tools_auth = Cookies.get('qttr_tools_auth');
  const qttr_tools_daypass_auth = Cookies.get('qttr_tools_daypass_auth');
  const [keyword, setKeyword] = useState('');
  const [description, setDescription] = useState('');
  const [numTags, setNumTags] = useState(3);
  const [generatedTitles, setGeneratedTitles] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [captchaError, setCaptchaError] = useState('');
  const [creditsNeeded, setCreditsNeeded] = useState(1);
  const inputSelectionRef = useRef<HTMLInputElement>(null);
  const session = useSession();

  const handleRecaptchaChange = (value) => {
    setRecaptchaValue(value);
    setCaptchaError('');
  };

  useEffect(() => {
    trackAnalyticsEvent({
      isAnonymousUser: !user,
      payload: {
        label: 'Visited Free Tool',
        component_name: COMPONENT_NAMES.contentai_ai_tools,
        screen_name: SCREEN_NAMES.contentai_ai_tools,
        tool_name: CONTENT_AI_TOOL_NAME.seo_friendly_url_generator,
        tool_category: CONTENT_AI_TOOL_CATEGORY.free_seo_tools,
        isQwaDashboard: isQwaDashboard,
      },
    });
  }, []);

  const handleWriteTitleTag = async () => {
    try {
      setLoading(true);
      if (!keyword) {
        setError('keyword');
        return;
      }
      if (!(recaptchaValue || qttr_tools_auth || user)) {
        setCaptchaError('recaptchaValue');
        return;
      }
      setCaptchaError('');
      setError('');

      const res = await fetchAIBasedContent({
        promptFor: 'PUBLIC_UTILITY.GENERATE_SEO_FRIENDLY_URL',
        promptConfig: { noOf: numTags, specialInstructions: description, topic: keyword },
        tokenLimit: 3500,
        isPublicApi: true,
        reCAPTCHAToken: recaptchaValue,
      });
      if (res) {
        trackAnalyticsEvent({
          eventFor: 'button',
          buttonText: '',
          counterName: `${CONTENT_AI_TOOL_CATEGORY.free_seo_tools} Outputs`,
          isAddCount: true,
          user,
          isAnonymousUser: !user,
          payload: {
            label: 'Generate Output',
            component_name: COMPONENT_NAMES.contentai_ai_tools,
            screen_name: SCREEN_NAMES.contentai_ai_tools,
            tool_name: CONTENT_AI_TOOL_NAME.seo_friendly_url_generator,
            tool_category: CONTENT_AI_TOOL_CATEGORY.free_seo_tools,
            number_suggestions: numTags,
            special_instructions_yn: description ? 'Yes' : 'No',
            isQwaDashboard: isQwaDashboard,
            creditsConsumed: creditsNeeded ? creditsNeeded : 1,
          },
        });
        if (handleResetUsage) {
          handleResetUsage();
        }
      }
      setGeneratedTitles(res || 'no title generated');
      if (resetLockedGrace) {
        resetLockedGrace();
        setNumTags(3);
      }
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError?.response) {
        if (axiosError.response.status === 403) {
          setShowSignUpPage(CONTINUE_USING_TEXT, {
            tool_name: CONTENT_AI_TOOL_NAME.seo_friendly_url_generator,
            tool_category: CONTENT_AI_TOOL_CATEGORY.free_seo_tools,
          });
          trackAnalyticsEvent({
            eventFor: 'button',
            isAnonymousUser: !user,
            payload: {
              label: 'Reached your maximum usage limit',
              component_name: COMPONENT_NAMES.contentai_ai_tools,
              screen_name: SCREEN_NAMES.contentai_ai_tools,
              tool_name: CONTENT_AI_TOOL_NAME.seo_friendly_url_generator,
              tool_category: CONTENT_AI_TOOL_CATEGORY.free_seo_tools,
              isQwaDashboard: isQwaDashboard,
            },
          });
        }
      }
      console.error('handleWriteTitleTag::error', error);
    } finally {
      setRecaptchaValue(null);
      resetCaptcha();
      setLoading(false);
    }
  };

  const handleCopyText = () => {
    const inputElement = inputSelectionRef?.current as any;
    if (inputElement) {
      const copiedText = inputElement?.value?.substring(
        inputElement?.selectionStart || 0,
        inputElement?.selectionEnd || 0
      );
      if (copiedText) {
        navigator.clipboard.writeText(copiedText);
        setCopied(true);
        trackAnalyticsEvent({
          eventFor: 'button',
          buttonText: '',
          counterName: 'Outputs Accepted',
          isAddCount: true,
          user,
          isAnonymousUser: !user,
          payload: {
            label: 'Copy Generated Output',
            component_name: COMPONENT_NAMES.contentai_ai_tools,
            screen_name: SCREEN_NAMES.contentai_ai_tools,
            tool_name: CONTENT_AI_TOOL_NAME.seo_friendly_url_generator,
            tool_category: CONTENT_AI_TOOL_CATEGORY.free_seo_tools,
            number_suggestions: numTags,
            special_instructions_yn: description ? 'Yes' : 'No',
            isQwaDashboard: isQwaDashboard,
            isManualCopy: true,
          },
        });
      } else {
        navigator.clipboard.writeText(generatedTitles);
        setCopied(true);
        trackAnalyticsEvent({
          eventFor: 'button',
          buttonText: '',
          counterName: 'Outputs Accepted',
          isAddCount: true,
          user,
          isAnonymousUser: !user,
          payload: {
            label: 'Copy Generated Output',
            component_name: COMPONENT_NAMES.contentai_ai_tools,
            screen_name: SCREEN_NAMES.contentai_ai_tools,
            tool_name: CONTENT_AI_TOOL_NAME.seo_friendly_url_generator,
            tool_category: CONTENT_AI_TOOL_CATEGORY.free_seo_tools,
            number_suggestions: numTags,
            special_instructions_yn: description ? 'Yes' : 'No',
            isQwaDashboard: isQwaDashboard,
          },
        });
      }
    }

    setTimeout(() => {
      setCopied(false);
    }, 2000); // Change the text back to 'Copy Text' after 2 seconds
  };

  const numbers = [3, 5, 10];

  // Convert the array items to a mixed array of a number and objects
  const NumberOptions = numbers.map((number) => {
    // Keep the first item as a number
    if ([3].includes(number)) {
      return number;
    }

    // Convert the rest to objects, include a React component for the icon
    return {
      name: number,
      requireLogin: !hasValidSubscription && !enableLockedOption,
      icon:
        !hasValidSubscription && !enableLockedOption ? (
          <LockOpenIcon className={classes.unlockIcon} />
        ) : (
          <PremiumToolsIcon />
        ), // This is a JSX element representing the icon
    };
  });

  useEffect(() => {
    const promptFor = PAID_TOOLS_PROMPT_FOR.SEO_FRIENDLY_URL_GENERATOR;
    const promptConfig = { noOf: numTags, specialInstructions: description, topic: keyword };
    setCreditsNeeded(getToolsUsageCreditsNeeded(promptFor, promptConfig));
  }, [user, numTags, description, keyword]);

  return (
    <>
      {generatedTitles ? (
        <div className={classes.container}>
          <TextField
            inputRef={inputSelectionRef}
            fullWidth
            multiline
            rows={8}
            margin="normal"
            placeholder="Generated Title Tag"
            value={generatedTitles}
            className={`${classes.textAreaInput} no-border background-gray`}
            maxRows={8}
            InputProps={{
              style: { borderRadius: '4px !important', padding: '16px' },
            }}
            minRows={5}
            variant="outlined"
            onCopy={handleCopyText}
          />
          <div className={classes.buttonGroup} style={{ marginTop: '16px' }}>
            <Button
              className={`${classes.toolsYellowCTA} ${classes.toolsOutlinedCTA} ${classes.button}`}
              disabled={loading}
              style={{ float: 'right' }}
              onClick={() => {
                setGeneratedTitles('');
                trackAnalyticsEvent({
                  eventName: 'Clicked on Go back',
                  payload: {
                    label: 'Clicked on Go back',
                  },
                });
              }}
            >
              Go back
            </Button>
            <Button
              className={`${classes.toolsYellowCTA} ${classes.button}`}
              disabled={loading}
              style={{ float: 'right' }}
              onClick={handleCopyText}
            >
              {copied ? 'Copied!' : 'Copy Text'}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className={classes.container}>
            {loading && <QuattrLogoSpinner classes={classes} />}
            <TextField
              fullWidth
              label="Enter Page Title or Topic"
              value={keyword}
              className={`${classes.textAreaInput} no-border mb-0`}
              onChange={(e) => setKeyword(e.target.value)}
              InputProps={{
                style: { backgroundColor: '#fff', borderRadius: '4px !important' },
              }}
              variant="outlined"
              error={!!error}
              helperText={error.includes('keyword') ? '* Required' : error.includes('Please sign up') ? error : ''}
            />

            <TextField
              fullWidth
              placeholder={`Unoptimized URL or any URL from your website\n\n(This will help us understand the URL style of your domain)`}
              multiline
              className={classes.textAreaInput}
              value={description}
              maxRows={5}
              InputProps={{
                style: { backgroundColor: '#fff', borderRadius: '4px !important', padding: '16px' },
                disableUnderline: true,
              }}
              minRows={5}
              variant="filled"
              onChange={(e) => setDescription(e.target.value)}
            />

            <CustomerDropDownWithLock
              label="Number of URL Suggestions"
              value={numTags}
              options={NumberOptions}
              onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                const value = event.target.value as number;
                const option = NumberOptions.find((o) => {
                  return typeof o === 'object' && 'name' in o ? o.name === value : o === value;
                });
                if (option && (typeof option === 'number' || !(typeof option == 'object' && option.requireLogin))) {
                  setNumTags(value);
                } else {
                  if (setShowSignUpPage) {
                    setShowSignUpPage(`'enable ${value} URL Suggestions'`, {
                      tool_name: CONTENT_AI_TOOL_NAME.seo_friendly_url_generator,
                      tool_category: CONTENT_AI_TOOL_CATEGORY.free_seo_tools,
                    });
                    trackAnalyticsEvent({
                      eventFor: 'button',
                      isAnonymousUser: !user,
                      payload: {
                        label: 'Clicked Locked Option',
                        component_name: COMPONENT_NAMES.contentai_ai_tools,
                        screen_name: SCREEN_NAMES.contentai_ai_tools,
                        tool_name: CONTENT_AI_TOOL_NAME.seo_friendly_url_generator,
                        tool_category: CONTENT_AI_TOOL_CATEGORY.free_seo_tools,
                        option_name: `${value} URL Suggestions`,
                        isQwaDashboard: isQwaDashboard,
                      },
                    });
                  }
                }
                // setError('');
              }}
              classes={classes}
            />
            {!qttr_tools_auth && !user && (
              <>
                <ReCAPTCHA
                  className={classes.recaptcha}
                  sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
                  onChange={handleRecaptchaChange}
                />
                {captchaError?.includes('recaptchaValue') && (
                  <FormHelperText className="red">Please complete the reCAPTCHA to verify you're human.</FormHelperText>
                )}
                <SkipCaptcha
                  classes={classes}
                  meta={{
                    component_name: COMPONENT_NAMES.contentai_ai_tools,
                    screen_name: SCREEN_NAMES.contentai_ai_tools,
                    tool_name: CONTENT_AI_TOOL_NAME.seo_friendly_url_generator,
                    tool_category: CONTENT_AI_TOOL_CATEGORY.free_seo_tools,
                  }}
                  setShowVerifyEmailPopup={setShowVerifyEmailPopup}
                />
              </>
            )}

            <div className={`${isCreditsShown ? classes.creditsShown : classes.freeCreditsShown}`}>
              {isCreditsShown ? (
                <CreditsInfo
                  classes={classes}
                  creditsRemaining={creditsRemaining}
                  creditsNeeded={creditsNeeded}
                  usagePerDay={usagePerDay}
                />
              ) : !isQwaDashboard || showFreeToolsUsage ? (
                <CreditsInfo
                  classes={classes}
                  creditsRemaining={freeToolsUsage}
                  creditsNeeded={creditsNeeded}
                  usagePerDay={isCorpEmail ? 10 : 7}
                  showFreeToolsUsage={showFreeToolsUsage}
                />
              ) : null}

              <div className={`d-flex ${classes.buttonGroup}`} style={{ marginTop: '16px' }}>
                <Button
                  style={{ float: 'right' }}
                  className={`${classes.toolsYellowCTA} ${classes.toolsOutlinedCTA} ${classes.button}`}
                  onClick={() => {
                    setKeyword('');
                    setDescription('');
                    setGeneratedTitles('');
                    setNumTags(3);
                  }}
                >
                  Clear
                </Button>
                <Button
                  className={`${classes.toolsYellowCTA} ${classes.button}`}
                  color="primary"
                  onClick={() =>
                    handleGenerateOutput(
                      creditsRemaining,
                      creditsNeeded,
                      isQwaDashboard,
                      session,
                      qttr_tools_daypass_auth,
                      setShowMinimumCreditsNeededMsg,
                      setShowBuyOnePass,
                      handleWriteTitleTag,
                      trackAnalyticsEvent,
                      freeToolsUsage,
                      showFreeToolsUsage
                    )
                  }
                >
                  Generate SEO-Friendly URL
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default SEOFriendlyURL;
